/**
 * Patient Card Component
 * Compact card view for displaying patient information in list/grid views
 */

import { User, Phone, Calendar, CheckCircle } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import type { DentalOfficePatient } from '../../types';
import { formatKennitala, formatDate } from '../../lib/utils';

interface PatientCardProps {
  patient: DentalOfficePatient;
}

export function PatientCard({ patient }: PatientCardProps) {
  const navigate = useNavigate();

  const initials = `${patient.first_name[0]}${patient.last_name[0]}`;
  const fullName = `${patient.first_name} ${patient.last_name}`;

  return (
    <div
      onClick={() => navigate(`/patients/${patient.id}`)}
      className="bg-white rounded-xl shadow-soft p-6 hover:shadow-medium transition-all cursor-pointer group"
    >
      <div className="flex items-start gap-4">
        {/* Avatar */}
        <div className="w-12 h-12 bg-brand-primary/10 rounded-full flex items-center justify-center flex-shrink-0 group-hover:bg-brand-primary/20 transition-colors">
          <span className="text-brand-primary font-semibold text-lg">
            {initials}
          </span>
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-2 mb-2">
            <h3 className="font-semibold text-gray-900 truncate group-hover:text-brand-primary transition-colors">
              {fullName}
            </h3>
            {patient.myhealth_user_id && (
              <CheckCircle className="w-5 h-5 text-success flex-shrink-0" />
            )}
          </div>

          <div className="space-y-1 text-sm text-gray-600">
            <div className="flex items-center gap-2">
              <User className="w-4 h-4" />
              <span className="font-mono">{patient.kennitala && formatKennitala(patient.kennitala)}</span>
            </div>

            {patient.phone && (
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4" />
                <span>{patient.phone}</span>
              </div>
            )}

            {patient.last_visit_date && (
              <div className="flex items-center gap-2">
                <Calendar className="w-4 h-4" />
                <span>Síðasta heimsókn: {formatDate(patient.last_visit_date!)}</span>
              </div>
            )}
          </div>

          {/* Status Badge */}
          {!patient.is_active && (
            <div className="mt-3">
              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
                Óvirkur
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
